// apps/client/src/components/FeatureGate.tsx
// Feature gating component for subscription tier-based access control
//
// Access model (first match wins, fails closed):
//   1. Disabled flag on registry → always deny
//   2. Client-side fast path: useSubscriptionTier (tier + allowedCompanyIds + active)
//   3. Server-authoritative fallback: useFeatureEntitlement → checkFeatureEntitlement
//      callable, which reads companies/{companyId}/entitlements/config for:
//        - addOnFeatures[flag].enabled (paid Stripe add-on)
//        - promotionalFeatures[flag].enabled (beta grant with expiresAt)
//        - featureFlags[flag] on company doc (legacy override)
//      Purpose: let betas like Broker Activity activate without a code deploy.
//      See docs/runbooks/BROKER-ACTIVITY-BETA-GRANT.md for the grant mechanism.

import React, { ReactNode, useEffect, useRef } from 'react';
import { useSubscriptionTier } from '../hooks/useSubscriptionTier';
import { useFeatureEntitlement } from '../hooks/useFeatureEntitlement';
import { useAppContext } from './layout/AppLayout';
import {
  Feature,
  FEATURE_REGISTRY,
  featureRequiresExplicitGrant,
} from '../config/subscriptionTiers';
import UpgradePromptCard from './UpgradePromptCard';
import Loading from './common/Loading';
import { posthog } from '../providers/PostHogProvider';

interface FeatureGateProps {
  /** The feature identifier to check (primary; drives the upgrade prompt copy) */
  feature: Feature;
  /**
   * Optional UNION alternate: access is granted if `feature` OR this alternate
   * grants access (tier or entitlement). Used to decouple a report from a legacy
   * add-on without stripping tenants that hold the add-on. Supports a single
   * alternate at the entitlement layer (e.g. trail_leakage with broker_activity).
   */
  anyOf?: Feature;
  /** Content to render if feature is available */
  children: ReactNode;
  /** Optional custom fallback instead of upgrade prompt */
  fallback?: ReactNode;
  /** Show loading state while checking */
  showLoading?: boolean;
}

/**
 * FeatureGate - Conditionally render content based on subscription tier
 *
 * Usage:
 * ```tsx
 * <FeatureGate feature="office_dashboard">
 *   <OfficeDashboard />
 * </FeatureGate>
 * ```
 *
 * With custom fallback:
 * ```tsx
 * <FeatureGate feature="ai_assistant" fallback={<ComingSoon />}>
 *   <AIAssistant />
 * </FeatureGate>
 * ```
 */
export const FeatureGate: React.FC<FeatureGateProps> = ({
  feature,
  anyOf,
  children,
  fallback,
  showLoading = false,
}) => {
  const { hasFeature, getRequiredTier, loading: tierLoading } = useSubscriptionTier();
  const { companyData } = useAppContext();
  const companyId: string | undefined = companyData?.id || companyData?.companyId;
  const featureConfig = FEATURE_REGISTRY[feature];
  const requiredTier = getRequiredTier(feature);

  // Tier check covers the primary feature AND the optional union alternate.
  const explicitGrantRequired = featureRequiresExplicitGrant(feature);
  const tierGrantsAccess = explicitGrantRequired
    ? false
    : hasFeature(feature) || (Boolean(anyOf) && hasFeature(anyOf as Feature));

  // Server-authoritative entitlement check (reads addOnFeatures + promotionalFeatures).
  // Only consulted when the client-side tier check denies — avoids an RPC for every
  // tier-default feature. Skipped entirely when the registry marks the feature disabled.
  const shouldCheckEntitlement = !featureConfig?.disabled && !tierGrantsAccess && Boolean(companyId);
  const { hasFeature: primaryEntitlement, loading: primaryEntitlementLoading } = useFeatureEntitlement(
    shouldCheckEntitlement ? companyId : undefined,
    feature
  );
  // Union alternate entitlement check (fixed hook slot; inert unless `anyOf` set).
  const { hasFeature: altEntitlement, loading: altEntitlementLoading } = useFeatureEntitlement(
    shouldCheckEntitlement && anyOf ? companyId : undefined,
    anyOf ?? feature
  );

  const entitlementGrantsAccess = primaryEntitlement || (Boolean(anyOf) && altEntitlement);
  const entitlementLoading = primaryEntitlementLoading || (Boolean(anyOf) && altEntitlementLoading);

  const loading = tierLoading || (shouldCheckEntitlement && entitlementLoading);
  const canAccess = tierGrantsAccess || (shouldCheckEntitlement && entitlementGrantsAccess);

  // Track when a feature gate blocks access (fire once per mount)
  const capturedRef = useRef(false);
  useEffect(() => {
    if (!canAccess && !loading && !capturedRef.current) {
      capturedRef.current = true;
      try { posthog?.capture('subscription_feature_blocked', { feature, requiredTier }); } catch {}
    }
  }, [canAccess, loading, feature, requiredTier]);

  // Disabled features are completely hidden (no upgrade prompt)
  if (featureConfig?.disabled) return null;

  // Show loading state if requested
  if (loading && showLoading) {
    return <Loading />;
  }

  // Check if user has access to this feature
  if (canAccess) {
    return <>{children}</>;
  }

  // Show custom fallback if provided
  if (fallback) {
    return <>{fallback}</>;
  }

  // Show upgrade prompt
  return (
    <div className="min-h-[400px] flex items-center justify-center p-8">
      <div className="max-w-xl w-full">
        <UpgradePromptCard
          featureName={featureConfig.name}
          description={featureConfig.description}
          requiredTier={requiredTier}
          benefits={featureConfig.benefits}
        />
      </div>
    </div>
  );
};

/**
 * useFeatureAccess - Hook to check feature access without rendering
 *
 * Usage:
 * ```tsx
 * const { canAccess, requiredTier } = useFeatureAccess('office_dashboard');
 *
 * if (!canAccess) {
 *   navigate('/settings/billing');
 * }
 * ```
 */
export function useFeatureAccess(feature: Feature) {
  const { hasFeature, getRequiredTier, tier, isActive } = useSubscriptionTier();

  return {
    canAccess: hasFeature(feature),
    requiredTier: getRequiredTier(feature),
    currentTier: tier,
    isActive,
    featureConfig: FEATURE_REGISTRY[feature],
  };
}

export default FeatureGate;
